import React, { createContext, useContext, useMemo, ReactNode } from 'react';

// Types
export interface UserRole {
    id: number;
    name: string;
    slug: string;
}

export interface PermissionContextType {
    permissions: string[];
    roles: UserRole[];
    hasPermission: (permission: string) => boolean;
    hasAnyPermission: (permissions: string[]) => boolean;
    hasAllPermissions: (permissions: string[]) => boolean;
    hasRole: (roleSlug: string) => boolean;
    isSuperAdmin: boolean;
    isAdmin: boolean;
    /** L'utilisateur est rattaché à une facility GROUPE (≠ « admin du groupe »). Pour l'affichage des features groupe, JAMAIS pour octroyer des permissions. */
    isOnGroupFacility: boolean;
}

interface PermissionProviderProps {
    permissions: string[];
    roles: UserRole[];
    isSuperAdmin: boolean;
    isAdmin: boolean;
    isOnGroupFacility: boolean;
    children: ReactNode;
}

// Context
const PermissionContext = createContext<PermissionContextType | null>(null);

// Provider
export const PermissionProvider: React.FC<PermissionProviderProps> = ({
    permissions,
    roles,
    isSuperAdmin,
    isAdmin,
    isOnGroupFacility,
    children,
}) => {
    // Seuls super-admin et admin ont tous les droits, comme côté backend
    // (CheckPermission ne laisse passer que is_super_admin et is_admin). NE PAS
    // inclure isOnGroupFacility : « être sur une facility groupe » n'est pas un
    // rôle — l'y mettre donnait tous les droits à n'importe quel staff du groupe
    // (escalade de privilèges, cf. audit vue groupe).
    const hasAllRights = isSuperAdmin || isAdmin || permissions.includes('*');

    const hasPermission = useMemo(() => (permission: string): boolean => {
        // Super admin ou admin de groupe a toutes les permissions
        if (hasAllRights) {
            return true;
        }
        return permissions.includes(permission);
    }, [permissions, hasAllRights]);

    const hasAnyPermission = useMemo(() => (perms: string[]): boolean => {
        if (hasAllRights) {
            return true;
        }
        return perms.some(p => permissions.includes(p));
    }, [permissions, hasAllRights]);

    const hasAllPermissions = useMemo(() => (perms: string[]): boolean => {
        if (hasAllRights) {
            return true;
        }
        return perms.every(p => permissions.includes(p));
    }, [permissions, hasAllRights]);

    const hasRole = useMemo(() => (roleSlug: string): boolean => {
        return roles.some(r => r.slug === roleSlug);
    }, [roles]);

    const value = useMemo(() => ({
        permissions,
        roles,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        hasRole,
        isSuperAdmin,
        isAdmin,
        isOnGroupFacility,
    }), [permissions, roles, hasPermission, hasAnyPermission, hasAllPermissions, hasRole, isSuperAdmin, isAdmin, isOnGroupFacility]);

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
};

// Hook
export const usePermissions = (): PermissionContextType => {
    const context = useContext(PermissionContext);
    if (!context) {
        // Retourner un contexte par défaut si non initialisé (pour éviter les erreurs)
        return {
            permissions: [],
            roles: [],
            hasPermission: () => false,
            hasAnyPermission: () => false,
            hasAllPermissions: () => false,
            hasRole: () => false,
            isSuperAdmin: false,
            isAdmin: false,
            isOnGroupFacility: false,
        };
    }
    return context;
};

// Export par défaut
export default PermissionContext;
